import React from 'react';
import useGameBase from '../../hooks/useGameBase';

function NumberGuessGame({ gameState, gamePlayers, user, onGameAction, isSpectator, onPlayAgain, socket, roomId }) {
  const { focusRef, findPlayerById, isLocalPlayerTurn, submitAction, normalizedState } = useGameBase({ gameState, gamePlayers, user, socket, roomId });

  const min = typeof normalizedState.min === 'number' ? normalizedState.min : 1;
  const max = typeof normalizedState.max === 'number' ? normalizedState.max : 10;
  const isFinished = !!normalizedState.finished;

  return (
    <div className="mt-4 bg-white dark:bg-gray-800 rounded shadow p-4 text-gray-900 dark:text-gray-100">
      {/* Instructions only if not finished */}
      {!isFinished && (
        <p className="text-gray-700 dark:text-gray-200">Guess a number between {min} and {max}. Each player takes turns.</p>
      )}

      {/* Winner and secret number reveal */}
      {isFinished && (
        <div className="mt-4 text-xl bg-gray-100 dark:bg-gray-700 rounded p-3">
          <p className="mt-2 text-lg text-gray-800 dark:text-gray-100">The secret number was <span className="font-bold">{normalizedState.secret}</span>.</p>
          <p className="mt-2 text-base text-gray-700 dark:text-gray-200">
            It took <span className="font-bold">{Array.isArray(normalizedState.guesses) ? normalizedState.guesses.length : '?'}</span> guess{Array.isArray(normalizedState.guesses) && normalizedState.guesses.length === 1 ? '' : 'es'} to find the correct answer.
          </p>
        </div>
      )}

      {/* Input and Guess button only if not finished */}
      {!isFinished && !isSpectator && (
        <>
          {!isLocalPlayerTurn() ? (
            (() => {
              const currentPlayer = findPlayerById(normalizedState.currentPlayerSocketId || normalizedState.currentPlayer);
              return (
                <div className="mt-3 mb-2 text-base text-gray-700 dark:text-gray-200 flex items-center">
                  <span style={{ backgroundColor: currentPlayer?.color || '#e57373', borderRadius: '4px', padding: '2px 6px', marginRight: '6px', display: 'inline-block' }}>
                    {currentPlayer?.icon || '😀'}
                  </span>
                  <span className="font-semibold">{currentPlayer?.username || 'A player'} is making a guess...</span>
                </div>
              );
            })()
          ) : (
            <form
              className="flex mt-4"
              onSubmit={e => {
                e.preventDefault();
                const value = e.target.elements.guess.value;
                if (value) {
                  submitAction(Number(value)).then(() => {
                    try { e.target.reset(); } catch (e) {}
                  });
                }
              }}
            >
              <input
                ref={focusRef}
                type="number"
                name="guess"
                min={min}
                max={max}
                className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 p-2 flex-1 mr-2 focus:outline-none focus:ring-2 focus:ring-blue-300 dark:focus:ring-blue-400"
              />
              <button
                type="submit"
                className="bg-blue-500 hover:bg-blue-600 dark:bg-blue-700 dark:hover:bg-blue-800 text-white p-2 rounded transition-colors duration-150"
              >
                Guess
              </button>
            </form>
          )}
        </>
      )}


      {/* Pulsate animation keyframes */}
      <style>{`
        @keyframes yourTurnPulse {
          0% { filter: brightness(1.1) drop-shadow(0 0 0 #ef4444); }
          50% { filter: brightness(1.3) drop-shadow(0 0 8px #ef4444); }
          100% { filter: brightness(1.1) drop-shadow(0 0 0 #ef4444); }
        }
      `}</style>

     

      {/* Show clue as animated arrow and latest guess if not finished and clue exists */}
    {!isFinished && normalizedState.clue && Array.isArray(normalizedState.guesses) && normalizedState.guesses.length > 0 && (
        <div className="flex flex-col items-center mt-6 mb-2 bg-gray-50 dark:bg-gray-900 rounded p-3 shadow-inner">
          {/* Animated arrow and label */}
      {normalizedState.clue === 'Higher' && (
            <div className="flex flex-col items-center">
              <span className="animate-bounce" style={{ fontSize: '3.5rem', color: '#22c55e', display: 'block', lineHeight: 1, textShadow: '0 0 8px #22c55e' }} title="Higher">
                &#8593;
              </span>
              <span className="text-green-600 dark:text-green-400 font-bold text-xl mt-1">Higher</span>
            </div>
          )}
          {normalizedState.clue === 'Lower' && (
            <div className="flex flex-col items-center">
              <span className="animate-bounce" style={{ fontSize: '3.5rem', color: '#ef4444', display: 'block', lineHeight: 1, textShadow: '0 0 8px #ef4444' }} title="Lower">
                &#8595;
              </span>
              <span className="text-red-600 dark:text-red-400 font-bold text-xl mt-1">Lower</span>
            </div>
          )}
          {/* Latest guess info */}
          {(() => {
            const g = normalizedState.guesses[normalizedState.guesses.length - 1];
            let guessValue;
            if (typeof g.value === 'object' && g.value !== null && 'index' in g.value) {
              guessValue = g.value.index;
            } else {
              guessValue = g.value;
            }
            const player = findPlayerById(g.playerId);
            return (
              <div className="flex items-center mt-2 text-lg text-gray-800 dark:text-gray-100">
                <span style={{ backgroundColor: player?.color || '#e57373', borderRadius: '4px', padding: '2px 6px', marginRight: '6px', display: 'inline-block' }}>
                  {player?.icon || '😀'}
                </span>
                <span className="font-semibold mr-2">{player?.username}:</span>
                <span className="font-mono text-xl">{guessValue}</span>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}


NumberGuessGame.Guide = function NumberGuessGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">How to Play Number Guess</h3>
      <ul className="list-disc ml-6">
        <li>Each player takes turns guessing a secret number.</li>
        <li>The number is between the min and max shown.</li>
        <li>First to guess the secret number wins.</li>
      </ul>
    </div>
  );
};

export default NumberGuessGame;
